import {
  FormProvider,
  useFormContext,
  useFormState,
  useWatch,
} from 'react-hook-form';
import { usePartApplicationForm } from '../hooks/usePartApplicationForm';
import PartyRegardingApplication from '../steps/Form/PartyRegardingApplication/PartyRegardingApplication';
import CompanyContactSection from '../steps/Form/CompanyContactSection/CompanyContactSection';
import SubmitSection from '../steps/Form/SubmitSection/SubmitSection';
import { useScrollToError } from '../../ApplicationV3/hooks/useScrollError';
import OtherInfo from '../steps/Form/OtherInfo/OtherInfo';
import UnionSection from '../steps/Form/UnionSection/UnionSection';
import ReportDismissedSection from '../steps/Form/ReportDismissedSection/ReportDismissedSection';
import StartSection from '../steps/Form/StartSection/StartSection';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { useMemo, useRef, useState } from 'react';
import { FormData } from '../validation/formSchema';
import { mapFormDataToSubmitDto } from '../mappers/mapFormDataToSubmitDto';
import { useWorksites } from '../../ApplicationV3/hooks/useWorksites';
import TextBlock from 'components/formComponents/Base/TextBlock';
import {
  useLazyGetExistingAutomatedBankruptcyIncidentQuery,
  useSubmitApplicationMutation,
} from '../partyApplicationApi';
import FullPageSpinner from 'components/common/FullPageSpinner/FullPageSpinner';
import {
  ApplicationSteps,
  ApplicationType,
} from '../../../types/PartyApplication/partyApplication';
import { useEffect } from 'react';
import { setCurrentStep } from '../../../redux/reducer/partyApplicationSlice';
import styles from './FormPage.module.scss';
import { DID_NOT_FIND_VALUE } from 'components/formComponents/Base/Dropdown';

const ScrollToError = () => {
  useScrollToError();
  return null;
};

const LiveSuperRefineValidation = () => {
  const { control, trigger, getFieldState } = useFormContext<FormData>();
  const { isSubmitted } = useFormState({ control });
  const isFirstRender = useRef(true);
  type FormFieldName = Parameters<typeof getFieldState>[0];

  const applicationType = useWatch({ control, name: 'applicationType' });
  const worksiteRegardingApplication = useWatch({
    control,
    name: 'worksiteRegardingApplication',
  });
  const companyContactEmail = useWatch({
    control,
    name: 'companyContactEmail',
  });
  const companyContactConfirmEmail = useWatch({
    control,
    name: 'companyContactConfirmEmail',
  });
  const unionContactEmail = useWatch({ control, name: 'unionContactEmail' });
  const unionContactConfirmEmail = useWatch({
    control,
    name: 'unionContactConfirmEmail',
  });
  const worksiteAddressCountry = useWatch({
    control,
    name: 'worksiteAddressCountry',
  });

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const hasInteracted = (name: FormFieldName) => {
      const state = getFieldState(name);
      return state.isTouched || state.isDirty;
    };

    const hasAnyError = (names: FormFieldName[]) =>
      names.some((name) => Boolean(getFieldState(name).error));

    const fieldsToTrigger = new Set<FormFieldName>();
    const addFields = (names: FormFieldName[]) => {
      names.forEach((name) => fieldsToTrigger.add(name));
    };

    const companyContactFields: FormFieldName[] = [
      'companyContactFirstname',
      'companyContactLastname',
      'companyContactPhone',
      'companyContactEmail',
      'companyContactConfirmEmail',
    ];

    const shouldRequireCompanyContact =
      applicationType ===
        ApplicationType.dismissedDueToRedundancyOrContractExpiry ||
      applicationType === ApplicationType.dismissedDueToIllnessOrInjury;

    if (shouldRequireCompanyContact) {
      if (
        isSubmitted ||
        companyContactFields.some((field) => hasInteracted(field))
      ) {
        addFields(companyContactFields);
      }
    } else if (hasAnyError(companyContactFields)) {
      // Revalidate to clear stale conditional errors when this section is no longer required.
      addFields(companyContactFields);
    }

    const unionEmailFields: FormFieldName[] = [
      'unionContactEmail',
      'unionContactConfirmEmail',
    ];
    if (isSubmitted || unionEmailFields.some((field) => hasInteracted(field))) {
      addFields(unionEmailFields);
    }

    const manualWorksiteFields: FormFieldName[] = [
      'worksitename',
      'worksiteAddress',
      'worksitePostalCity',
      'worksitePostalCode',
      'worksiteTelephone',
      'worksiteAddressCountry',
    ];

    if (worksiteRegardingApplication === DID_NOT_FIND_VALUE) {
      if (
        isSubmitted ||
        manualWorksiteFields.some((field) => hasInteracted(field))
      ) {
        addFields(manualWorksiteFields);
      }
    } else if (hasAnyError(manualWorksiteFields)) {
      // Revalidate to clear stale conditional errors when manual worksite is deselected.
      addFields(manualWorksiteFields);
    }

    if (fieldsToTrigger.size > 0) {
      void trigger(Array.from(fieldsToTrigger));
    }
  }, [
    applicationType,
    worksiteRegardingApplication,
    companyContactEmail,
    companyContactConfirmEmail,
    unionContactEmail,
    unionContactConfirmEmail,
    worksiteAddressCountry,
    getFieldState,
    isSubmitted,
    trigger,
  ]);

  return null;
};

const FormPage = () => {
  const dispatch = useAppDispatch();
  const { applicantRole } = useAppSelector((state) => state.partyApplication);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [
    isSubmittingExistingBankruptcyContact,
    setIsSubmittingExistingBankruptcyContact,
  ] = useState(false);
  const [existingBankruptcyIncidentId, setExistingBankruptcyIncidentId] =
    useState<string | null>(null);
  const [hasExistingBankruptcyIncident, setHasExistingBankruptcyIncident] =
    useState(false);
  const [
    isCheckingExistingBankruptcyIncident,
    setIsCheckingExistingBankruptcyIncident,
  ] = useState(false);
  const [
    existingBankruptcyIncidentLookupFailed,
    setExistingBankruptcyIncidentLookupFailed,
  ] = useState(false);
  const [submitApplication, submitApplicationResult] =
    useSubmitApplicationMutation();
  const [loadExistingBankruptcyIncident] =
    useLazyGetExistingAutomatedBankruptcyIncidentQuery();
  const { pageTexts, applicationType, partyRegardingApplication } =
    useAppSelector((state) => state.partyApplication);
  const { worksites } = useWorksites(partyRegardingApplication || undefined);
  const requiredConsentIds = useMemo(() => {
    const ids = pageTexts?.submitSection?.requiredConsentCheckboxes?.map(
      (checkbox) => String(checkbox.id)
    );
    return ids && ids.length > 0 ? ids : ['constentBox1'];
  }, [pageTexts]);

  const methods = usePartApplicationForm(
    undefined,
    requiredConsentIds,
    applicantRole ?? undefined,
    pageTexts
  );
  useEffect(() => {
    const checkExistingIncident = async () => {
      if (
        applicationType !== ApplicationType.companyBankruptcy ||
        !partyRegardingApplication
      ) {
        setExistingBankruptcyIncidentId(null);
        setHasExistingBankruptcyIncident(false);
        setExistingBankruptcyIncidentLookupFailed(false);
        setIsCheckingExistingBankruptcyIncident(false);
        return;
      }

      setIsCheckingExistingBankruptcyIncident(true);
      setExistingBankruptcyIncidentLookupFailed(false);

      try {
        const response = await loadExistingBankruptcyIncident(
          partyRegardingApplication
        ).unwrap();

        const incidentId = response.incidentId ?? null;
        setExistingBankruptcyIncidentId(incidentId);
        setHasExistingBankruptcyIncident(Boolean(response.hasExistingIncident));
      } catch {
        setExistingBankruptcyIncidentId(null);
        setHasExistingBankruptcyIncident(false);
        setExistingBankruptcyIncidentLookupFailed(true);
      } finally {
        setIsCheckingExistingBankruptcyIncident(false);
      }
    };

    void checkExistingIncident();
  }, [
    applicationType,
    loadExistingBankruptcyIncident,
    partyRegardingApplication,
  ]);

  const isPostingApplication = useMemo(() => {
    return submitApplicationResult.isLoading;
  }, [submitApplicationResult.isLoading]);

  const fullPageLoadingText = useMemo(() => {
    if (isSubmittingExistingBankruptcyContact) {
      return (
        pageTexts?.submitSection?.existingBankruptcyContactSubmittingInfo ||
        'Skickar kontaktuppgifter...'
      );
    }

    return 'Skapar ansökan...';
  }, [isSubmittingExistingBankruptcyContact, pageTexts]);

  const showExistingBankruptcyFlow = useMemo(() => {
    return (
      applicationType === ApplicationType.companyBankruptcy &&
      hasExistingBankruptcyIncident &&
      Boolean(existingBankruptcyIncidentId)
    );
  }, [
    applicationType,
    existingBankruptcyIncidentId,
    hasExistingBankruptcyIncident,
  ]);

  const resolveSubmitErrorMessage = (error: unknown): string => {
    if (typeof error === 'object' && error !== null) {
      const typedError = error as {
        data?: { message?: string };
        message?: string;
      };
      if (typedError.data?.message) {
        return typedError.data.message;
      }
      if (typedError.message) {
        return typedError.message;
      }
    }

    return 'Ansokan kunde inte skickas in just nu. Forsok igen om en stund.';
  };

  const handleSubmit = async (data: FormData) => {
    setSubmitError(null);

    if (hasExistingBankruptcyIncident) {
      setSubmitError(
        'Det finns redan en konkursansökan för valt företag. Lägg till dig som företagskontakt i stället för att skicka in en ny ansökan.'
      );
      return;
    }

    try {
      const selectedWorksite = worksites?.find(
        (worksite) => worksite.id === data.worksiteRegardingApplication
      );
      const payload = mapFormDataToSubmitDto(data, selectedWorksite);
      await submitApplication(payload).unwrap();
      dispatch(setCurrentStep(ApplicationSteps.Confirmation));
      return;
    } catch (error) {
      setSubmitError(resolveSubmitErrorMessage(error));
    }
  };

  const handleExistingBankruptcyContactSubmitted = () => {
    dispatch(setCurrentStep(ApplicationSteps.ExistingBankruptcyConfirmation));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key !== 'Enter') return;

    const target = e.target;

    if (
      target instanceof HTMLInputElement ||
      target instanceof HTMLSelectElement
    ) {
      e.preventDefault();
      target.blur();
    }
  };

  return (
    <FormProvider {...methods}>
      {isPostingApplication ? (
        <FullPageSpinner loadingText={fullPageLoadingText} />
      ) : isSubmittingExistingBankruptcyContact ? (
        <FullPageSpinner loadingText={fullPageLoadingText} />
      ) : (
        <>
          <ScrollToError />
          <LiveSuperRefineValidation />
          <form
            className={styles.formLayout}
            onSubmit={methods.handleSubmit(handleSubmit)}
            onKeyDown={handleKeyDown}
          >
            <StartSection />
            <PartyRegardingApplication
              isCheckingExistingBankruptcyIncident={
                isCheckingExistingBankruptcyIncident
              }
            />
            {!showExistingBankruptcyFlow && <CompanyContactSection />}
            {!showExistingBankruptcyFlow && <UnionSection />}
            {!showExistingBankruptcyFlow && <ReportDismissedSection />}
            {!showExistingBankruptcyFlow && <OtherInfo />}
            <SubmitSection
              showExistingBankruptcyFlow={showExistingBankruptcyFlow}
              existingBankruptcyIncidentId={
                existingBankruptcyIncidentId ?? undefined
              }
              isCheckingExistingBankruptcyIncident={
                isCheckingExistingBankruptcyIncident
              }
              existingBankruptcyIncidentLookupFailed={
                existingBankruptcyIncidentLookupFailed
              }
              onExistingBankruptcyContactSubmitted={
                handleExistingBankruptcyContactSubmitted
              }
              onExistingBankruptcyContactSubmittingChange={(isSubmitting) => {
                setIsSubmittingExistingBankruptcyContact(isSubmitting);
              }}
            />
            {submitError && (
              <TextBlock
                type="error"
                text={submitError}
                dataCy="party-application-submit-error"
              />
            )}
          </form>
        </>
      )}
    </FormProvider>
  );
};

export default FormPage;
